import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import DateField from '../../components/Fields/DateField';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig, FormConfig2 } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ItemTypeModel from '../../models/ItemTypeModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import ItemType_tab_AttachmentTypes from './Tabs/ItemType_tab_AttachmentTypes';
import ItemType_tab_BomRestrictions from './Tabs/ItemType_tab_BomRestrictions';
import ItemType_tab_BomTemplates from './Tabs/ItemType_tab_BomTemplates';
import ItemType_tab_ItemStatusTypes from './Tabs/ItemType_tab_ItemStatusTypes';
import ItemType_tab_ProjectTypes from './Tabs/ItemType_tab_ProjectTypes';
import ItemType_tab_UserDefinedFields from './Tabs/ItemType_tab_UserDefinedFields';
import ItemType_tab_WorkflowDefinitions from './Tabs/ItemType_tab_WorkflowDefinitions';
import ItemType_tab_WorkflowRoles from './Tabs/ItemType_tab_WorkflowRoles';

const ItemTypeView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<ItemTypeModel>>({ isSuccesful: true, messages: [], data: undefined });
    const attachmentTypes = useSelectListOptions('api/v1/selectlist/AttachmentTypes', '-- Select attachment type');
    const associationTypes = useSelectListOptions('api/v1/selectlist/AssociationTypes', '-- Select association type');
    const itemStatusTypes = useSelectListOptions('api/v1/selectlist/ItemStatusTypes', '-- Select main itemstatus type');
    const newItemStatusModes = useSelectListOptions('api/v1/selectlist/NewItemRevisionStatusModes', '-- Select new item status mode');
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [formData, setFormData] = useState<ItemTypeModel>();

    let idParams: IdParams = useParams();
    let itemTypeId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        itemTypeId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (itemTypeId === undefined) {
            setFormData({
                id: 0,
                code: '',
                name: '',
                productCategoryAssociationTypeId: 0,
                brandAssociationTypeId: 0,
                mainItemStatusTypeId: 0,
                newItemRevisionStatusModeId: 0,
                importable: true,
                exportable: true,
                searchable: true,
                defaultReuse: false,
                showInBom: true,
                hasBom: true,
                hasColors: false,
                hasSizes: false,
                hasLicenses: false,
                hasPrices: false,
                hasDesignPhases: false,
                hasBusinessPartners: false,
                hasCustomerSKU: false,
                hasLicensorSKU: false,
                createSubItemPerColor: false,
                beginDate: new FormatUtils().formatDateForStorage(new Date()),
                endDate: '',

                concurrencyId: ''
            })
        }
        else {
            DataService.getById<ItemTypeModel>('api/v1/itemtypes/', itemTypeId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [itemTypeId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<ItemTypeModel>(`api/v1/itemtypes/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<ItemTypeModel>(`api/v1/itemtypes/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'code', label: 'Code', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'name', label: 'Name', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'productCategoryAssociationTypeId', label: 'Relation to Product category', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'brandAssociationTypeId', label: 'Relation to Brand', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'mainItemStatusTypeId', label: 'Main item-status type', labelSize: 5 },
            { name: 'defaultDownloadAttachmentTypeId', label: 'Default attachment type for download from BOM', labelSize: 5 },
            { name: 'newItemRevisionStatusModeId', label: 'New item-status mode', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', labelSize: 5, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 8, validationRules: [{ type: 'isDate' }] },
            { name: 'importable', label: 'Importable', labelSize: 8 },
            { name: 'exportable', label: 'Exportable', labelSize: 8 },
            { name: 'searchable', label: 'Searchable', labelSize: 8 },
            { name: 'defaultReuse', label: 'Items of this type are only usable as items in a BOM.', labelSize: 8 },
            { name: 'showInBom', label: 'Show in Bill-of-Materials', labelSize: 8 },
            { name: 'hasBom', label: 'Has a Bill of Material', labelSize: 8 },
            { name: 'hasColors', label: 'Has Colors', labelSize: 8 },
            { name: 'hasSizes', label: 'Has Sizes', labelSize: 8 },
            { name: 'hasLicenses', label: 'Has Licenses', labelSize: 8 },
            { name: 'hasPrices', label: 'Has Prices', labelSize: 8 },
            { name: 'hasDesignPhases', label: 'Has Design phases', labelSize: 8 },
            { name: 'hasBusinessPartners', label: 'Has Business-partners', labelSize: 8 },
            { name: 'hasCustomerSKU', label: 'Has Customer SKU', labelSize: 8 },
            { name: 'hasLicensorSKU', label: 'Has Licensor SKU', labelSize: 8 },
            { name: 'createSubItemPerColor', label: 'Create Sub-Item per Color', labelSize: 8 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (itemTypeId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditItemType)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Item type' footer={(
                <Row>
                    <Col md='8'>
                        <ButtonBar offset={5}>
                            <Authorize applicationFunction={ApplicationFunctions.EditItemType} itemTypeId={itemTypeId}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                    <Col md='4'>
                        <ButtonBar offset={0}>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('code')} />
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('productCategoryAssociationTypeId')} options={associationTypes} />
                            <SelectListField {...getFieldProps('brandAssociationTypeId')} options={associationTypes} />
                            <SelectListField {...getFieldProps('mainItemStatusTypeId')} options={itemStatusTypes} />
                            <SelectListField {...getFieldProps('defaultDownloadAttachmentTypeId')} options={attachmentTypes} />
                            <SelectListField {...getFieldProps('newItemRevisionStatusModeId')} options={newItemStatusModes} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>

                        </Col>
                        <Col md='3'>
                            <SwitchField {...getFieldProps('importable')} />
                            <SwitchField {...getFieldProps('exportable')} />
                            <SwitchField {...getFieldProps('searchable')} />
                            <SwitchField {...getFieldProps('defaultReuse')} />
                            <SwitchField {...getFieldProps('showInBom')} />
                            <SwitchField {...getFieldProps('hasCustomerSKU')} />
                            <SwitchField {...getFieldProps('hasLicensorSKU')} />
                        </Col>
                        <Col md='3'>
                            <SwitchField {...getFieldProps('hasBom')} />
                            <SwitchField {...getFieldProps('hasColors')} />
                            <SwitchField {...getFieldProps('hasSizes')} />
                            <SwitchField {...getFieldProps('hasLicenses')} />
                            <SwitchField {...getFieldProps('hasPrices')} />
                            <SwitchField {...getFieldProps('hasDesignPhases')} />
                            <SwitchField {...getFieldProps('hasBusinessPartners')} />
                            <SwitchField {...getFieldProps('createSubItemPerColor')} />
                        </Col>

                    </Row>
                </Container>
            </Form>
            {formData && itemTypeId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='user-defined fields'
                                tabs={[
                                    {
                                        title: 'User-defined fields',
                                        eventKey: 'user-defined fields',
                                        component: <ItemType_tab_UserDefinedFields itemTypeId={itemTypeId} ></ItemType_tab_UserDefinedFields>,
                                        applicationFunction: ApplicationFunctions.ViewUdf
                                    },
                                    {
                                        title: 'Itemstatus types',
                                        eventKey: 'itemstatustypes',
                                        component: <ItemType_tab_ItemStatusTypes itemTypeId={itemTypeId} ></ItemType_tab_ItemStatusTypes>,
                                    },
                                    {
                                        title: 'Workflows',
                                        eventKey: 'workflows',
                                        component: <ItemType_tab_WorkflowDefinitions itemTypeId={itemTypeId} ></ItemType_tab_WorkflowDefinitions>,
                                        applicationFunction: ApplicationFunctions.ViewWorkflowDefinition
                                    },
                                    {
                                        title: 'Roles',
                                        eventKey: 'workflowroles',
                                        component: <ItemType_tab_WorkflowRoles itemTypeId={itemTypeId} ></ItemType_tab_WorkflowRoles>,
                                        applicationFunction: ApplicationFunctions.ViewFunctionalRole
                                    },
                                    {
                                        title: 'Project types',
                                        eventKey: 'projecttypes',
                                        component: <ItemType_tab_ProjectTypes itemTypeId={itemTypeId} ></ItemType_tab_ProjectTypes>,
                                        applicationFunction: ApplicationFunctions.ViewProjectType
                                    },
                                    {
                                        title: 'Attachment types',
                                        eventKey: 'attachmenttypes',
                                        component: <ItemType_tab_AttachmentTypes itemTypeId={itemTypeId} ></ItemType_tab_AttachmentTypes>,
                                        applicationFunction: ApplicationFunctions.ViewAttachmentTypes
                                    },
                                    {
                                        title: 'BOM-restrictions',
                                        eventKey: 'bom-restrictions',
                                        visible: formData.hasBom,
                                        component: <ItemType_tab_BomRestrictions itemTypeId={itemTypeId} ></ItemType_tab_BomRestrictions>,
                                    },
                                    {
                                        title: 'BOM-templates',
                                        eventKey: 'bom-templates',
                                        visible: formData.hasBom,
                                        component: <ItemType_tab_BomTemplates itemType={formData} ></ItemType_tab_BomTemplates>,
                                        applicationFunction: ApplicationFunctions.ViewBomTemplate
                                    }
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default ItemTypeView;